import { ClipboardList, MapPinned, MonitorSmartphone, ShieldCheck, Users } from "lucide-react";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { guardianLoginUrl, platformModules, roleBenefits } from "@/lib/siteContent";

const platformColumns = [
  {
    title: "Admin dashboard",
    icon: MonitorSmartphone,
    body: "Monitor active journeys, vehicle readiness, incidents, and service status from a single operational view.",
  },
  {
    title: "Driver workflow",
    icon: ClipboardList,
    body: "Guide drivers through structured pickup, status, and handover steps with consistent digital prompts.",
  },
  {
    title: "Parent tracking",
    icon: MapPinned,
    body: "Offer reassurance through clear journey progress, expected milestones, and shareable tracking access.",
  },
  {
    title: "School interface",
    icon: Users,
    body: "Support front-office and SEN coordination teams with better visibility into arrivals, delays, and incidents.",
  },
];

export const PlatformPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="CareFleet Guardian"
        title="The platform that turns transport delivery into a structured safeguarding system."
        description="CareFleet Guardian is the digital layer that separates CareFleet from a standard transport provider. It gives each stakeholder the right level of visibility while helping operational teams run a clear, structured, and safeguarding-aware service with real-time oversight."
        highlights={["Admin dashboard", "Driver workflow", "Parent tracking", "School interface"]}
        actions={[
          { label: "Request Demo", to: "/contact", testId: "platform-hero-request-demo-button" },
          { label: "Login", href: guardianLoginUrl, testId: "platform-hero-login-button", variant: "outline", showArrow: false },
        ]}
        baseTestId="platform-hero"
        visual={
          <div className="dashboard-surface p-7">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-300" data-testid="platform-illustrative-label">
              Illustrative dashboard • Example operational view
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {platformModules.map((module) => (
                <div key={module.title} className="rounded-3xl border border-white/10 bg-white/5 p-5" data-testid={`platform-module-${module.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-sm text-slate-300">{module.title}</p>
                  <p className="mt-3 font-heading text-2xl font-semibold text-white">{module.value}</p>
                  <p className="mt-3 text-sm leading-6 text-slate-300">{module.detail}</p>
                </div>
              ))}
            </div>
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Platform views"
            title="One platform, four role-based experiences."
            description="Guardian makes transport more intelligible to every stakeholder without creating noise or clutter."
            testId="platform-views-heading"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            {platformColumns.map((item) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title}>
                  <div className="soft-card h-full p-8" data-testid={`platform-view-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{item.title}</h2>
                    <p className="mt-4 text-base leading-8 text-slate-600">{item.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Benefits"
            title="Designed to reduce ambiguity and improve operational clarity."
            description="Guardian improves transport service quality by replacing fragmented communication with structured updates and a consistent digital record."
            testId="platform-benefits-heading"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            {roleBenefits.map((role) => (
              <Reveal key={role.title}>
                <div className="rounded-[28px] border border-white/10 bg-white/5 p-8" data-testid={`platform-benefit-${role.title.toLowerCase()}`}>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-brand-teal">
                    <ShieldCheck className="h-4 w-4" />
                    {role.title}
                  </div>
                  <p className="mt-5 font-heading text-3xl font-semibold">Role-based visibility</p>
                  <p className="mt-4 text-base leading-8 text-slate-300">{role.summary}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};